import { walletNftsPlatformId, walletTokensPlatformId } from '@/constants';
import { useAppStore } from '@/store/app';
import { useDataStore } from '@/store/data';
import { PortfolioAssetO, PortfolioElementO } from '@/types';
import {
  getAssetsFromElement,
  getCollectiblesFromAssets,
  NetworkIdType,
  PortfolioAssetCollectible,
  sortAssetCollectibles,
} from '@sonarwatch/portfolio-core';
import { computed, ComputedRef } from 'vue';

export default function usePortfolioInfos(
  elements: ComputedRef<PortfolioElementO[]>,
) {
  const appStore = useAppStore();
  const dataStore = useDataStore();

  const filteredElements = computed(() => {
    return elements.value.filter(
      (e) =>
        e.value === null || (e.value && e.value >= appStore.minDisplayedValue),
    );
  });

  const totalValue = computed(() => {
    return filteredElements.value.reduce(
      (acc, asset) => acc + (asset.value || 0),
      0,
    );
  });

  const assetCollectibles: ComputedRef<PortfolioAssetCollectible[]> = computed(
    () => {
      const data: PortfolioAssetCollectible[] = [];
      filteredElements.value.forEach((element) => {
        const assets = getAssetsFromElement(element);
        const collectibles = getCollectiblesFromAssets(assets);
        data.push(...collectibles);
      });
      return data.sort(sortAssetCollectibles);
    },
  );

  const assetWalletTokens: ComputedRef<PortfolioAssetO[]> = computed(() => {
    const data: PortfolioAssetO[] = [];
    const elements = filteredElements.value.filter(
      (e) => e.platformId === walletTokensPlatformId,
    );
    elements.forEach((element) => {
      const assets = getAssetsFromElement(element);
      data.push(...assets.map((a) => ({ ...a, owner: element.owner })));
    });
    return data;
  });

  const portfolioByPlatform = computed(() => {
    const elements = filteredElements.value;
    const data: {
      platformId: string;
      value: number;
      elements: PortfolioElementO[];
      networkIds: Set<NetworkIdType>;
    }[] = [];
    elements.forEach((element) => {
      const platform = data.find((d) => d.platformId === element.platformId);
      const exclude = [walletTokensPlatformId, walletNftsPlatformId];
      if (exclude.includes(element.platformId)) return;
      if (!platform) {
        data.push({
          platformId: element.platformId,
          value: element.value || 0,
          elements: [element],
          networkIds: new Set([element.networkId]),
        });
      } else {
        platform.elements.push(element);
        platform.value += element.value || 0;
        platform.networkIds.add(element.networkId);
      }
    });
    return data;
  });

  const portfolioByNetwork = computed(() => {
    const elements = filteredElements.value;
    const data: {
      networkId: NetworkIdType;
      networkName?: string;
      networkImage?: string;
      value: number;
      elements: PortfolioElementO[];
    }[] = [];

    elements.forEach((element) => {
      const network = data.find((d) => d.networkId === element.networkId);
      if (!network) {
        data.push({
          networkId: element.networkId,
          networkName: dataStore.getNetworkById(element.networkId)?.name,
          networkImage: dataStore.getNetworkById(element.networkId)?.image,
          value: 0,
          elements: [],
        });
      }

      const index = data.findIndex((d) => d.networkId === element.networkId);

      data[index].elements.push(element);
      data[index].value += element.value || 0;
    });
    return data;
  });

  const elementsByNetworkId = computed(() => {
    return portfolioByNetwork.value.sort((a, b) => b.value - a.value);
  });

  const elementsByPlatformId = computed(() => {
    return portfolioByPlatform.value.sort((a, b) => b.value - a.value);
  });

  return {
    totalValue,
    elementsByNetworkId,
    elementsByPlatformId,
    assetCollectibles,
    assetWalletTokens,
  };
}
